import {
  ApiACLRole,
  ApiSimpleTenant,
  ApiTenantProvider,
} from "@incendium/api";
import { samlService } from "Apis";
import { getSelectedEnvironment } from "config";

/** Client/project pairs from the default (empty) group block. */
export function getSamlTenantProjectLinks(
  tenant: ApiSimpleTenant,
): Array<{ clientId: number; projectId: number }> {
  const seen = new Set<string>();
  const out: Array<{ clientId: number; projectId: number }> = [];
  for (const block of tenant.permissions || []) {
    if (block.group !== undefined && block.group !== "") {
      continue;
    }
    for (const p of block.permissions || []) {
      if (p.clientId == null || p.projectId == null) continue;
      const k = `${p.clientId}-${p.projectId}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ clientId: p.clientId, projectId: p.projectId });
    }
  }
  return out;
}

function dedupeProjectLinks(
  links: Array<{ clientId: number; projectId: number }>,
): Array<{ clientId: number; projectId: number }> {
  const seen = new Set<string>();
  const out: Array<{ clientId: number; projectId: number }> = [];
  for (const l of links) {
    const k = `${l.clientId}-${l.projectId}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(l);
  }
  return out;
}

/** Project admin on each linked project; single empty group (no IdP group claim). */
export function buildSamlTenantPermissions(
  projectLinks: Array<{ clientId: number; projectId: number }>,
): NonNullable<ApiSimpleTenant["permissions"]> {
  const environment = getSelectedEnvironment() || "";
  const links = dedupeProjectLinks(projectLinks);
  return [
    {
      group: "",
      permissions: links.map(({ clientId, projectId }) => ({
        clientId,
        projectId,
        role: ApiACLRole.PROJECT_ADMIN,
        environment,
      })),
    },
  ];
}

export function buildSamlTenantBody(
  fields: Pick<
    ApiSimpleTenant,
    "entityId" | "metaDataUrl" | "domain" | "name" | "enabled" | "provider"
  > & {
    projectLinks: Array<{ clientId: number; projectId: number }>;
  },
): ApiSimpleTenant {
  const { projectLinks, ...rest } = fields;
  return {
    ...rest,
    provider:
      rest.provider ?? ApiTenantProvider.TENANT_PROVIDER_MICROSOFT,
    permissions: buildSamlTenantPermissions(projectLinks),
  };
}

export const createSamlTenant = async (tenant: ApiSimpleTenant) => {
  const response = await samlService.samlServiceCreateTenant({
    body: tenant,
  });
  return response;
};

/** Path id is the internal tenant record id (`ApiSimpleTenant.id`), not the entity id. */
export const deleteSamlTenant = async (internalTenantId: string) => {
  const response = await samlService.samlServiceDeleteTenant({
    tenantId: internalTenantId,
  });
  return response;
};

export const listSamlTenants = async () => {
  const response = await samlService.samlServiceListTenants();
  return response;
};

/** Path id is the internal tenant record id (`ApiSimpleTenant.id`), not the entity id. */
export const updateSamlTenant = async (
  internalTenantId: string,
  tenant: ApiSimpleTenant,
) => {
  const response = await samlService.samlServiceUpdateTenant({
    tenantId: internalTenantId,
    body: tenant,
  });
  return response;
};
